// src/pages/admin/AdminSuggestions.js
//
// Rebuilt to actually match the real admin design system, now that
// it's been seen in full — wrapped in <AdminLayout>, using the
// shared .admin-page-header / .admin-eyebrow / .admin-page-title /
// .admin-filter-pills / .admin-pill / .admin-btn classes from
// AdminLayout.css instead of inventing parallel ones. Page-specific
// CSS (AdminSuggestions.css) only covers what those shared classes
// don't, same convention as AdminCreatorApprovals.css.
//
// Route: /admin/suggestions (add via App.js + AdminGate, per
// ADMIN-LAYOUT-HOME-DIFFS.md and the App.js route instructions).

import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import AdminLayout from "./AdminLayout";
import "./AdminSuggestions.css";

function timeSince(ts) {
  const millis = ts?.toDate ? ts.toDate().getTime() : ts?.seconds ? ts.seconds * 1000 : null;
  if (!millis) return "";
  const diff = Date.now() - millis;
  if (diff < 3600000) return `${Math.max(1, Math.floor(diff / 60000))}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

export default function AdminSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all"); // "all" | "creator" | "fan"
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "new" | "reviewed"
  const [sortOrder, setSortOrder] = useState("newest"); // "newest" | "oldest"

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "suggestions"), orderBy("createdAt", "desc")));
        if (!cancelled) {
          setSuggestions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error("AdminSuggestions load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const handleToggleReviewed = async (id, currentStatus) => {
    const nextStatus = currentStatus === "reviewed" ? "new" : "reviewed";
    setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, status: nextStatus } : s)));
    try {
      await updateDoc(doc(db, "suggestions", id), { status: nextStatus });
    } catch (err) {
      console.error("AdminSuggestions status update error:", err);
      setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, status: currentStatus } : s)));
    }
  };

  const filtered = useMemo(() => {
    let list = suggestions;
    if (roleFilter !== "all") list = list.filter((s) => s.role === roleFilter);
    if (statusFilter !== "all") list = list.filter((s) => (s.status || "new") === statusFilter);
    list = [...list].sort((a, b) => {
      const aMillis = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
      const bMillis = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
      return sortOrder === "newest" ? bMillis - aMillis : aMillis - bMillis;
    });
    return list;
  }, [suggestions, roleFilter, statusFilter, sortOrder]);

  const newCount = suggestions.filter((s) => (s.status || "new") === "new").length;
  const creatorCount = suggestions.filter((s) => s.role === "creator").length;
  const fanCount = suggestions.filter((s) => s.role === "fan").length;

  return (
    <AdminLayout>
      <div className="admin-shell asugg-shell">
        <div className="admin-header-row">
          <div className="admin-page-header">
            <div className="admin-eyebrow">Feedback</div>
            <div className="admin-page-title">Suggestions</div>
            <span className="admin-page-hand">— what creators and fans are actually asking for</span>
          </div>
          <div className="admin-stat-strip">
            <div className="admin-stat">
              <div className="admin-stat-num">{suggestions.length}</div>
              <div className="admin-stat-label">Total</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num gold">{newCount}</div>
              <div className="admin-stat-label">Unreviewed</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num">{creatorCount}</div>
              <div className="admin-stat-label">Creators</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num">{fanCount}</div>
              <div className="admin-stat-label">Fans</div>
            </div>
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-filter-pills">
            <button className={`admin-pill${roleFilter === "all" ? " active" : ""}`} onClick={() => setRoleFilter("all")}>All roles</button>
            <button className={`admin-pill${roleFilter === "creator" ? " active" : ""}`} onClick={() => setRoleFilter("creator")}>Creators</button>
            <button className={`admin-pill${roleFilter === "fan" ? " active" : ""}`} onClick={() => setRoleFilter("fan")}>Fans</button>
          </div>
          <div className="admin-filter-pills">
            <button className={`admin-pill${statusFilter === "all" ? " active" : ""}`} onClick={() => setStatusFilter("all")}>All</button>
            <button className={`admin-pill${statusFilter === "new" ? " active" : ""}`} onClick={() => setStatusFilter("new")}>New</button>
            <button className={`admin-pill${statusFilter === "reviewed" ? " active" : ""}`} onClick={() => setStatusFilter("reviewed")}>Reviewed</button>
          </div>
          <div className="admin-toolbar-right">
            <select className="admin-sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>
        </div>

        {loading && <div className="asugg-empty">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="asugg-empty">Nothing here yet.</div>}

        <div className="asugg-list">
          {filtered.map((s) => (
            <div key={s.id} className={`asugg-row${s.status === "reviewed" ? " reviewed" : ""}`}>
              <div className="asugg-row-top">
                <span className={`asugg-role-badge asugg-role-${s.role}`}>
                  {s.role === "creator" ? "Creator" : "Fan"}
                </span>
                <span className="asugg-row-name">{s.displayName}</span>
                <span className="asugg-row-time">{timeSince(s.createdAt)}</span>
              </div>
              <p className="asugg-row-text">{s.text}</p>
              <button
                type="button"
                className="admin-btn admin-btn-ghost"
                onClick={() => handleToggleReviewed(s.id, s.status || "new")}
              >
                {s.status === "reviewed" ? "Mark as new" : "Mark reviewed"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
